import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Chip,
  Paper,
  Divider,
  IconButton,
  Collapse,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  LocalPharmacy as PharmacyIcon,
  Science as LabIcon,
  Image as ImageIcon,
  Healing as ProcedureIcon,
  LocalHospitalOutlined as EmergencyIcon,
  Bed as BedIcon,
  MonitorHeart as VitalIcon,
  Note as NoteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewResultIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import ZoomableImageDialog from "../../../../../Components/GeneralComponents/ZoomableImageDialog/ZoomableImageDialog";
import TimelineSkeleton from "../../../../../Components/GeneralComponents/timelineSkeleton/TimelineSkeleton";

const getTypeIcon = (type) => {
  const iconProps = { sx: { fontSize: 24 } };
  switch (type) {
    case "encounter":
      return <AssignmentIcon {...iconProps} color="primary" />;
    case "prescription":
      return <PharmacyIcon {...iconProps} color="success" />;
    case "laboratory":
      return <LabIcon {...iconProps} color="info" />;
    case "xray":
    case "ultrasound":
    case "echo":
      return <ImageIcon {...iconProps} color="warning" />;
    case "procedure":
    case "surgery":
      return <ProcedureIcon {...iconProps} color="error" />;
    case "ipd_admission":
    case "icu_admission":
      return <BedIcon {...iconProps} color="secondary" />;
    case "emergency":
      return <EmergencyIcon {...iconProps} color="error" />;
    case "vital_signs":
      return <VitalIcon {...iconProps} color="primary" />;
    case "nursing_note":
      return <NoteIcon {...iconProps} color="info" />;
    default:
      return <HospitalIcon {...iconProps} />;
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case "encounter":
      return "primary";
    case "prescription":
      return "success";
    case "laboratory":
      return "info";
    case "xray":
    case "ultrasound":
    case "echo":
      return "warning";
    case "procedure":
    case "surgery":
      return "error";
    case "ipd_admission":
    case "icu_admission":
      return "secondary";
    case "emergency":
      return "error";
    case "vital_signs":
      return "primary";
    case "nursing_note":
      return "info";
    default:
      return "default";
  }
};

const UnifiedEHRTimeline = ({ patientId, defaultTypeFilter = "all" }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState(defaultTypeFilter);
  const [expandedItems, setExpandedItems] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [labOrderDetails, setLabOrderDetails] = useState({});
  const [labOrderLoading, setLabOrderLoading] = useState({});
  const [diagnosticDetails, setDiagnosticDetails] = useState({});
  const [diagnosticLoading, setDiagnosticLoading] = useState({});
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerSrc, setImageViewerSrc] = useState("");
  const [imageViewerTitle, setImageViewerTitle] = useState("");

  // Build query params
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.append("start_date", startDate);
  if (endDate) queryParams.append("end_date", endDate);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["unified-ehr", patientId, startDate, endDate],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/patients/${patientId}/unified-ehr/?${queryParams.toString()}`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => res.data),
    enabled: !!patientId && !!token,
  });

  // Auto-expand all items by default
  useEffect(() => {
    if (data?.timeline) {
      const allExpanded = {};
      data.timeline.forEach((item) => {
        allExpanded[item.id] = true;
      });
      setExpandedItems(allExpanded);
    }
  }, [data]);

  const timeline = data?.timeline || [];
  const patientName = data?.patient_name || "";

  // Filter timeline items
  const filteredTimeline = timeline.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      JSON.stringify(item.data).toLowerCase().includes(searchTerm.toLowerCase());

    let matchesType = false;
    if (typeFilter === "all") {
      matchesType = true;
    } else if (typeFilter === "imaging") {
      // Include all imaging types
      matchesType = ["xray", "ultrasound", "echo"].includes(item.type);
    } else {
      matchesType = item.type === typeFilter;
    }

    return matchesSearch && matchesType;
  });

  // Group by date
  const groupedByDate = filteredTimeline.reduce((acc, item) => {
    const dateKey = moment(item.date).format("YYYY-MM-DD");
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(item);
    return acc;
  }, {});

  const apiBase = process.env.REACT_APP_API_URL || "";
  const mediaBase = apiBase.replace(/\/?api\/?$/, "");
  const toAbsoluteMediaUrl = (url) => {
    if (!url) return url;
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const fetchLabOrderResults = async (orderId) => {
    if (labOrderDetails[orderId] || labOrderLoading[orderId]) return;
    setLabOrderLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/laboratory-test-orders/${orderId}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setLabOrderDetails((prev) => ({ ...prev, [orderId]: res.data }));
    } catch (err) {
      console.error("Failed to fetch lab order results", err);
    } finally {
      setLabOrderLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getDiagnosticKey = (type, id) => `${type}-${id}`;
  const fetchDiagnosticResult = async (type, id) => {
    const key = getDiagnosticKey(type, id);
    if (diagnosticDetails[key] || diagnosticLoading[key]) return;
    setDiagnosticLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const endpoints = {
        xray: "patient-xray",
        ultrasound: "ultrasounds",
        echo: "patient-echo",
      };
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${endpoints[type]}/${id}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setDiagnosticDetails((prev) => ({ ...prev, [key]: res.data }));
    } catch (err) {
      console.error("Failed to fetch diagnostic result", err);
    } finally {
      setDiagnosticLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const renderItemContent = (item) => {
    const { type, data } = item;

    switch (type) {
      case "encounter":
        return (
          <Box>
            {data.chief_complaint && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Chief Complaint:</strong> {data.chief_complaint}
              </Typography>
            )}
            {data.diagnosis && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Diagnosis:</strong> {data.diagnosis}
              </Typography>
            )}
            {data.notes && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Notes:</strong> {data.notes}
              </Typography>
            )}
            {data.clinical_notes && data.clinical_notes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Clinical Notes ({data.clinical_notes.length}):
                </Typography>
                {data.clinical_notes.map((note, idx) => (
                  <Paper key={idx} sx={{ p: 1, mb: 1, bgcolor: "grey.50" }}>
                    <Typography variant="caption" color="text.secondary">
                      {note.note_type_display} - {note.created_by} -{" "}
                      {moment(note.created_at).format("MMM DD, YYYY HH:mm")}
                    </Typography>
                    {note.subjective && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>S:</strong> {note.subjective}
                      </Typography>
                    )}
                    {note.objective && (
                      <Typography variant="body2">
                        <strong>O:</strong> {note.objective}
                      </Typography>
                    )}
                    {note.assessment && (
                      <Typography variant="body2">
                        <strong>A:</strong> {note.assessment}
                      </Typography>
                    )}
                    {note.plan && (
                      <Typography variant="body2">
                        <strong>P:</strong> {note.plan}
                      </Typography>
                    )}
                    {note.content && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {note.content}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        );

      case "prescription":
        return (
          <Box>
            {data.drugs && data.drugs.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Medications:
                </Typography>
                {data.drugs.map((drug, idx) => {
                  const medicineName =
                    typeof drug.medicine === "string"
                      ? drug.medicine
                      : drug.medicine?.name ||
                        drug.stock_medicine_display ||
                        drug.stock_medicine?.medicine?.name ||
                        "Unknown Medicine";
                  return (
                    <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                      • {medicineName} - Qty: {drug.quantity || "N/A"} -{" "}
                      {drug.dosage || "N/A"}
                    </Typography>
                  );
                })}
              </Box>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Total Price:</strong> {data.total_price} AFN
            </Typography>
            <Chip
              label={data.payment_status === 1 ? "Paid" : "Pending"}
              size="small"
              color={data.payment_status === 1 ? "success" : "warning"}
              sx={{ mt: 1 }}
            />
          </Box>
        );

      case "laboratory": {
        const orderId = item.id;
        const orderDetail = labOrderDetails[orderId];
        const loadingResult = labOrderLoading[orderId];
        const testsDisplay = orderDetail?.tests_display || [];
        return (
          <Box>
            {data.test_names_display && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t("profile.tests", "Test(s)")}:</strong> {data.test_names_display}
              </Typography>
            )}
            {data.test_names?.length > 0 && !data.test_names_display && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t("profile.tests", "Test(s)")}:</strong> {data.test_names.join(", ")}
              </Typography>
            )}
            {data.result_status_display && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t("profile.resultStatus", "Result status")}:</strong>{" "}
                <Chip
                  label={data.result_status_display}
                  size="small"
                  color={
                    data.result_status === "given"
                      ? "success"
                      : data.result_status === "in_progress"
                        ? "info"
                        : data.result_status === "cancelled"
                          ? "default"
                          : "warning"
                  }
                  variant="outlined"
                />
              </Typography>
            )}
            {data.doctor && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>{t("ipd.admissions.doctor", "Doctor")}:</strong> {data.doctor}
              </Typography>
            )}
            {!orderDetail ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={loadingResult ? <CircularProgress size={16} /> : <ViewResultIcon />}
                onClick={() => fetchLabOrderResults(orderId)}
                disabled={loadingResult}
              >
                {loadingResult
                  ? t("profile.loading", "Loading…")
                  : t("profile.viewResult", "View result")}
              </Button>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                  {t("profile.results", "Results")}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.100" }}>
                        <TableCell><strong>{t("profile.tests", "Test(s)")}</strong></TableCell>
                        <TableCell><strong>{t("profile.result", "Result")}</strong></TableCell>
                        <TableCell><strong>{t("profile.normalRange", "Normal range")}</strong></TableCell>
                        <TableCell><strong>{t("profile.units", "Units")}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testsDisplay.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" color="text.secondary">
                            {t("profile.noResults", "No tests in this order.")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        testsDisplay.map((test) => {
                          const tt = test.test_type_display || {};
                          const hasResult = tt.result_value != null && tt.result_value !== "";
                          const isParent =
                            !test.parent_test && test.child_tests?.length > 0;
                          const resultDisplay = hasResult
                            ? tt.result_value
                            : isParent
                              ? "—"
                              : (t("profile.pending", "Pending") || "Pending");
                          const normalRange =
                            tt.reference_range ||
                            (tt.min_value != null && tt.max_value != null
                              ? `${tt.min_value} - ${tt.max_value}`
                              : null) ||
                            "—";
                          return (
                            <TableRow key={test.id}>
                              <TableCell
                                sx={
                                  isParent ? { fontWeight: "bold" } : undefined
                                }
                              >
                                {tt.name || "—"}
                              </TableCell>
                              <TableCell>{resultDisplay}</TableCell>
                              <TableCell>{normalRange}</TableCell>
                              <TableCell>{tt.units || "—"}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        );
      }

      case "xray":
      case "ultrasound":
      case "echo": {
        const key = getDiagnosticKey(type, item.id);
        const detail = diagnosticDetails[key];
        const loading = diagnosticLoading[key];
        const resultData = detail?.result_data;
        const typeLabel =
          data.xray_type || data.ultrasound_type || data.echo_type;
        return (
          <Box>
            {typeLabel && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t("profile.type", "Type")}:</strong> {typeLabel}
              </Typography>
            )}
            {data.doctor && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>{t("ipd.admissions.doctor", "Doctor")}:</strong>{" "}
                {data.doctor}
              </Typography>
            )}
            {!detail ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ViewResultIcon />
                  )
                }
                onClick={() => fetchDiagnosticResult(type, item.id)}
                disabled={loading}
              >
                {loading
                  ? t("profile.loading", "Loading…")
                  : t("profile.viewResult", "View result")}
              </Button>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("profile.results", "Results")}
                </Typography>
                {resultData?.findings && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>
                      {t("profile.findings", "Findings")}:
                    </strong>{" "}
                    {resultData.findings}
                  </Typography>
                )}
                {resultData?.image && (
                  <Box
                    sx={{ mt: 1, cursor: "pointer" }}
                    onClick={() => {
                      setImageViewerSrc(
                        toAbsoluteMediaUrl(resultData.image)
                      );
                      setImageViewerTitle(
                        (typeLabel || type) +
                          (data.doctor ? ` - ${data.doctor}` : "")
                      );
                      setImageViewerOpen(true);
                    }}
                  >
                    <img
                      src={toAbsoluteMediaUrl(resultData.image)}
                      alt={type}
                      style={{
                        maxWidth: "100%",
                        maxHeight: 300,
                        borderRadius: 4,
                      }}
                    />
                  </Box>
                )}
                {!resultData?.findings && !resultData?.image && (
                  <Typography variant="body2" color="text.secondary">
                    {t("profile.noResults", "No results available.")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      }

      case "vital_signs":
        return (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {data.temperature && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Temp:</strong> {data.temperature}°C
                </Typography>
              </Grid>
            )}
            {data.systolic_bp && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>BP:</strong> {data.systolic_bp}/{data.diastolic_bp} mmHg
                </Typography>
              </Grid>
            )}
            {data.heart_rate && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>HR:</strong> {data.heart_rate} bpm
                </Typography>
              </Grid>
            )}
            {data.respiratory_rate && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>RR:</strong> {data.respiratory_rate} /min
                </Typography>
              </Grid>
            )}
            {data.oxygen_saturation && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>SpO2:</strong> {data.oxygen_saturation}%
                </Typography>
              </Grid>
            )}
            {data.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Notes:</strong> {data.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        );

      case "nursing_note":
        return (
          <Typography variant="body2">{data.content}</Typography>
        );

      case "ipd_admission":
      case "icu_admission":
        return (
          <Box>
            {data.ward && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Ward:</strong> {data.ward}
              </Typography>
            )}
            {data.room && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Room:</strong> {data.room}
              </Typography>
            )}
            {data.bed && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Bed:</strong> {data.bed}
              </Typography>
            )}
            {data.discharge_date && (
              <Typography variant="body2">
                <strong>Discharged:</strong>{" "}
                {moment(data.discharge_date).format("MMM DD, YYYY")}
              </Typography>
            )}
          </Box>
        );

      default:
        // Format unknown data types as a readable list
        const formatValue = (value) => {
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "object" && !Array.isArray(value)) {
            // If it's an object, try to extract readable fields
            if (value.first_name || value.last_name) {
              return `${value.first_name || ""} ${value.last_name || ""}`.trim();
            }
            if (value.name) return value.name;
            if (value.id) return `ID: ${value.id}`;
            // Otherwise, show key-value pairs
            return Object.entries(value)
              .filter(([k, v]) => v !== null && v !== undefined && typeof v !== "object")
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ");
          }
          if (Array.isArray(value)) {
            return value.length > 0 ? `${value.length} items` : "None";
          }
          return String(value);
        };

        return (
          <Box>
            {Object.entries(data)
              .filter(([key, value]) => value !== null && value !== undefined)
              .map(([key, value]) => (
                <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}:</strong>{" "}
                  {formatValue(value)}
                </Typography>
              ))}
          </Box>
        );
    }
  };

  if (isLoading) {
    return <TimelineSkeleton />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading timeline: {error?.message || "Unknown error"}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Unified EHR Timeline
              {patientName && ` - ${patientName}`}
            </Typography>

            {/* Filters */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                placeholder="Search timeline..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="encounter">Encounters</MenuItem>
                  <MenuItem value="prescription">Prescriptions</MenuItem>
                  <MenuItem value="laboratory">Laboratory</MenuItem>
                  <MenuItem value="imaging">Imaging (All)</MenuItem>
                  <MenuItem value="xray">X-Ray</MenuItem>
                  <MenuItem value="ultrasound">Ultrasound</MenuItem>
                  <MenuItem value="echo">Echo</MenuItem>
                  <MenuItem value="procedure">Procedures</MenuItem>
                  <MenuItem value="surgery">Surgeries</MenuItem>
                  <MenuItem value="ipd_admission">IPD Admissions</MenuItem>
                  <MenuItem value="icu_admission">ICU Admissions</MenuItem>
                  <MenuItem value="emergency">Emergency</MenuItem>
                  <MenuItem value="vital_signs">Vital Signs</MenuItem>
                  <MenuItem value="nursing_note">Nursing Notes</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {filteredTimeline.length} of {timeline.length} items
            </Typography>
          </Box>

          {/* Timeline */}
          {Object.keys(groupedByDate).length === 0 ? (
            <Alert severity="info">No timeline items found</Alert>
          ) : (
            <Box>
              {Object.keys(groupedByDate)
                .sort((a, b) => moment(b).diff(moment(a)))
                .map((dateKey) => (
                  <Box key={dateKey} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        pb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      <CalendarIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      {moment(dateKey).format("MMMM DD, YYYY")}
                    </Typography>

                    {groupedByDate[dateKey].map((item) => (
                      <Paper
                        key={item.id}
                        elevation={2}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderLeft: `4px solid`,
                          borderColor: `${getTypeColor(item.type)}.main`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box sx={{ mt: 0.5 }}>{getTypeIcon(item.type)}</Box>
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography variant="h6">{item.title}</Typography>
                                {item.subtitle && (
                                  <Typography variant="body2" color="text.secondary">
                                    {item.subtitle}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {moment(item.date).format("HH:mm")}
                                  {item.data?.provider &&
                                    ` • ${typeof item.data.provider === "string" ? item.data.provider : item.data.provider.name || "Provider"}`}
                                  {item.data?.doctor &&
                                    ` • Dr. ${typeof item.data.doctor === "string" ? item.data.doctor : `${item.data.doctor.first_name || ""} ${item.data.doctor.last_name || ""}`.trim() || "Doctor"}`}
                                  {item.data?.nurse &&
                                    ` • Nurse: ${typeof item.data.nurse === "string" ? item.data.nurse : `${item.data.nurse.first_name || ""} ${item.data.nurse.last_name || ""}`.trim() || "Nurse"}`}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                <Chip
                                  label={item.type.replace("_", " ")}
                                  size="small"
                                  color={getTypeColor(item.type)}
                                  variant="outlined"
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => toggleExpand(item.id)}
                                >
                                  {expandedItems[item.id] ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Box>
                            </Box>

                            <Collapse in={expandedItems[item.id]}>
                              <Divider sx={{ my: 1 }} />
                              {renderItemContent(item)}
                            </Collapse>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <ZoomableImageDialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageSrc={imageViewerSrc}
        title={imageViewerTitle}
      />
    </Box>
  );
};

export default UnifiedEHRTimeline;
